import React, { useState } from 'react';
import { FaEdit } from 'react-icons/fa';
import { BiMap, BiPhone, BiMailSend, BiInfoCircle, BiTrash, BiError } from 'react-icons/bi';

const CustomerTable = ({ customers, getCustomerCalls, onEdit, onDelete, startIndex }) => {
  const [deleteId, setDeleteId] = useState(null);

  const getUsageColor = (used, limit) => {
    if (!limit) return 'bg-emerald-500';
    const percentage = (used / limit) * 100;
    if (percentage > 90) return 'bg-rose-500';
    if (percentage > 70) return 'bg-amber-500';
    return 'bg-emerald-500';
  };

  const handleDeleteClick = (customerId) => {
    setDeleteId(customerId);
  };

  const confirmDelete = () => {
    if (deleteId) {
      onDelete(deleteId);
      setDeleteId(null);
    }
  };

  return (
    <div className="w-full overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50/80 backdrop-blur-sm sticky top-0 z-10">
          <tr>
            <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-widest">#</th>
            <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-widest">Client Identity</th>
            <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-widest">Contact Info</th>
            <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-widest">Details</th>
            <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-widest w-64">Usage Overview</th>
            <th className="px-6 py-4 text-center text-xs font-bold text-gray-500 uppercase tracking-widest">Calls</th>
            <th className="px-6 py-4 text-center text-xs font-bold text-gray-500 uppercase tracking-widest">Actions</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-100">
          {customers.map((customer, index) => {
            const used = customer.callsUsed || 0;
            const limit = customer.callLimit;
            const remaining = limit === null ? '∞' : Math.max(0, limit - used);
            const usagePercentage = limit ? Math.min(100, (used / limit) * 100) : 0;

            const minsUsed = customer.minutesUsed || 0;
            const minsLimit = customer.totalMinuteLimit;
            const minsPercentage = minsLimit ? Math.min(100, (minsUsed / minsLimit) * 100) : 0;

            const clientCalls = getCustomerCalls(customer._id || customer.id);

            return (
              <tr
                key={customer._id || customer.id || index}
                className="group hover:bg-slate-50/80 transition-all duration-200"
              >
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-400">
                  {startIndex + index + 1}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex flex-col">
                    <span className="text-sm font-bold text-gray-900 group-hover:text-indigo-600 transition-colors">
                      {customer.name}
                    </span>
                    <span className="text-xs text-gray-400 font-mono mt-0.5">
                      ID: {customer._id ? customer._id.slice(-6) : 'New'}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-1.5 text-sm text-gray-600">
                      <BiMailSend className="text-gray-400" />
                      {customer.email}
                    </div>
                    <div className="flex items-center gap-1.5 text-sm text-gray-600">
                      <BiPhone className="text-gray-400" />
                      {customer.mobileNumber}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-1.5 text-sm text-gray-600">
                      <BiMap className="text-gray-400 flex-shrink-0" />
                      <span className="truncate max-w-[150px]">{customer.location || 'N/A'}</span>
                    </div>
                    <div className="flex items-start gap-1.5 text-sm text-gray-500 italic">
                      <BiInfoCircle className="text-gray-400 flex-shrink-0 mt-0.5" />
                      <span className="line-clamp-1 max-w-[200px] text-xs">
                        {customer.description || 'No description'}
                      </span>
                    </div>
                    {customer.minuteLimitPerCall && (
                      <div className="mt-1 flex items-center gap-1">
                        <span className="px-1.5 py-0.5 bg-amber-50 text-amber-600 text-[10px] font-bold rounded uppercase border border-amber-100">
                          {customer.minuteLimitPerCall}m / Call
                        </span>
                      </div>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex flex-col gap-3 min-w-[180px]">
                    {/* Calls Usage */}
                    <div className="space-y-1">
                      <div className="flex justify-between text-[10px] font-bold uppercase tracking-wider">
                        <span className="text-gray-400">Calls</span>
                        <span className="text-gray-700">{used} / {limit === null ? '∞' : limit}</span>
                      </div>
                      <div className="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all duration-500 ${getUsageColor(used, limit)}`}
                          style={{ width: `${limit ? usagePercentage : 100}%` }}
                        ></div>
                      </div>
                    </div>

                    {/* Minutes Usage */}
                    <div className="space-y-1">
                      <div className="flex justify-between text-[10px] font-bold uppercase tracking-wider">
                        <span className="text-gray-400">Minutes</span>
                        <span className="text-gray-700">{minsUsed} / {minsLimit === null ? '∞' : minsLimit}</span>
                      </div>
                      <div className="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all duration-500 ${getUsageColor(minsUsed, minsLimit)}`}
                          style={{ width: `${minsLimit ? minsPercentage : 100}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-center">
                  <div className="inline-flex items-center justify-center px-2.5 py-1 rounded-full bg-indigo-50 text-indigo-700 text-xs font-bold">
                    {clientCalls}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-center">
                  <div className="flex items-center justify-center gap-2">
                    <button
                      onClick={() => onEdit(customer)}
                      className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-white rounded-lg transition-all border border-transparent hover:border-gray-200 shadow-none hover:shadow-sm"
                      title="Edit Client"
                    >
                      <FaEdit size={16} />
                    </button>
                    <button
                      onClick={() => handleDeleteClick(customer._id)}
                      className="p-2 text-gray-400 hover:text-rose-600 hover:bg-white rounded-lg transition-all border border-transparent hover:border-gray-200 shadow-none hover:shadow-sm"
                      title="Delete Client"
                    >
                      <BiTrash size={18} />
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Modern Delete Confirmation Modal */}
      {deleteId && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-sm overflow-hidden animate-in fade-in zoom-in duration-300">
            <div className="p-8 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-rose-50 text-rose-500 rounded-2xl mb-6">
                <BiError size={32} />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Delete Client?</h3>
              <p className="text-gray-500 text-sm leading-relaxed px-4">
                This action cannot be undone. All client data and preferences will be permanently removed.
              </p>
            </div>
            <div className="flex p-4 gap-3 bg-gray-50/50">
              <button
                onClick={() => setDeleteId(null)}
                className="flex-1 px-4 py-3 text-gray-500 font-bold hover:bg-gray-100 rounded-2xl transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="flex-1 px-4 py-3 bg-rose-600 text-white font-bold rounded-2xl hover:bg-rose-700 shadow-lg shadow-rose-100 transition-all"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerTable;
